/**
 * AnnouncementBar — thin ticker above the header. Content from
 * marketing.announcement_bar[0]. Dismissible per-session.
 * If the store is closed, also surfaces the closed reason as a distinct strip.
 */
import { useEffect, useState } from "react";
import { X, Megaphone } from "lucide-react";
import { useStore } from "@/contexts/StoreContext";
import { Link } from "react-router-dom";

const DISMISS_KEY = "bb_annc_dismissed_v1";

export default function AnnouncementBar() {
  const { announcement, isOpen, closedReason } = useStore();
  const [dismissedTitle, setDismissedTitle] = useState("");

  useEffect(() => {
    try { setDismissedTitle(sessionStorage.getItem(DISMISS_KEY) || ""); } catch { /* noop */ }
  }, []);

  // Priority 1: store-closed strip.
  if (!isOpen) {
    return (
      <div className="w-full bg-destructive text-destructive-foreground text-xs md:text-sm" data-testid="closed-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-1.5 flex items-center gap-2 justify-center text-center font-medium">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span>{closedReason || "Store is currently closed."}</span>
        </div>
      </div>
    );
  }

  if (!announcement) return null;
  if (dismissedTitle === announcement.title) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, announcement.title || ""); } catch { /* noop */ }
    setDismissedTitle(announcement.title || "");
  };

  const body = (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-1.5 flex items-center gap-3 text-center justify-center">
      <Megaphone className="w-3.5 h-3.5 shrink-0" />
      <div className="text-xs md:text-sm font-medium truncate">
        <span className="font-semibold">{announcement.title}</span>
        {announcement.subtitle && <span className="ml-2 opacity-90">{announcement.subtitle}</span>}
      </div>
      <button
        onClick={dismiss}
        className="ml-auto opacity-80 hover:opacity-100"
        aria-label="Dismiss announcement"
        data-testid="announcement-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  return (
    <div className="w-full bg-foreground text-background" data-testid="announcement-bar">
      {announcement.link_to
        ? <Link to={announcement.link_to} className="block hover:opacity-95 transition">{body}</Link>
        : body}
    </div>
  );
}
